import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { toast } from "react-toastify";
import {
  fetchUniqueIds,
  sendFCMNotification,
} from "../Services/NotificationServices.js";
import { useAuth } from "./AuthContext.js";
import { url } from "../Constant/TextConstant.js";
import { apiFetch } from "./apiClient.js";

const CartContext = createContext();
const baseApiURL = url.baseAPIUrl;

const toastButtonStyle = (bg = "#3498db", color = "#fff") => ({
  padding: "8px 14px",
  background: bg,
  color: color,
  border: "none",
  borderRadius: "6px",
  fontWeight: "600",
  cursor: "pointer",
  fontSize: "14px",
  transition: "background 0.3s ease",
});

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem("cart");
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const [abandonmentTimer, setAbandonmentTimer] = useState(null);
  const [lastAddedItem, setLastAddedItem] = useState(null);
  const { currentUser } = useAuth();
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    if (!currentUser?.mobile) return;

    const fetchCartFromServer = async () => {
      try {
        setIsSyncing(true);
        const response = await apiFetch(
          `${baseApiURL}/api/cartitems/GetCartItems/${currentUser}`
        );
        if (!response.ok) throw new Error("Failed to fetch cart");
        const serverCart = await response.json();

        setCart((prev) => {
          const localCart = isInitialized
            ? prev
            : JSON.parse(localStorage.getItem("cart")) || [];
          const mergedCart = serverCart.map((serverItem) => {
            const localItem = localCart.find(
              (item) => item.ID === serverItem.ProductId
            );
            return localItem ? { ...localItem, ...serverItem } : serverItem;
          });
          return mergedCart;
        });
      } catch (error) {
        if (!isInitialized) {
          const savedCart = localStorage.getItem("cart");
          if (savedCart) {
            setCart(JSON.parse(savedCart));
          }
        }
      } finally {
        setIsInitialized(true);
        setIsSyncing(false);
      }
    };

    fetchCartFromServer();
  }, [currentUser]);

  useEffect(() => {
    return () => {
      if (abandonmentTimer) {
        clearTimeout(abandonmentTimer);
      }
    };
  }, [abandonmentTimer]);

  const sendAbandonmentNotification = useCallback(async () => {
    if (!lastAddedItem || !currentUser) return;
    try {
      const uniqueIds = await fetchUniqueIds(currentUser);
      if (uniqueIds) {
        await sendFCMNotification({
          Tokens: uniqueIds,
          Title: "Your cart is waiting!",
          Body: `Don't forget to complete your purchase from ${lastAddedItem.StoreName}`,
          ScreenRoute: "/cartScreen",
        });
      }
    } catch (error) {
    }
  }, [lastAddedItem, currentUser]);

  const startAbandonmentTimer = useCallback(() => {
    if (abandonmentTimer) clearTimeout(abandonmentTimer);
    const timer = setTimeout(() => sendAbandonmentNotification(), 10000);
    setAbandonmentTimer(timer);
  }, [abandonmentTimer, sendAbandonmentNotification]);

  const resetAbandonmentTimer = useCallback(() => {
    if (abandonmentTimer) {
      clearTimeout(abandonmentTimer);
      setAbandonmentTimer(null);
    }
  }, [abandonmentTimer]);

  const syncCartItemWithServer = async (action, item, extraData = {}) => {
    if (!currentUser) return;

    try {
      let url, options;
      const headers = { "Content-Type": "application/json" };

      switch (action) {
        case "add":
          url = `${baseApiURL}/api/cartitems/AddCart`;
          options = {
            method: "POST",
            headers,
            body: JSON.stringify({
              CustomerMobile: currentUser,
              StoreName: item.StoreName,
              StoreMobile: item.StoreMobile,
              Category: item.Category,
              SubCategory1: item.SubCategory1,
              SubCategory2: item.SubCategory2,
              ProductId: item.ID,
              ProductCode: item.ProductCode,
              ProductName: item.ProductName,
              ProductTitle: item.ProductTitle,
              Cashback: item.Cashback || 0,
              Size: item.Size || "",
              ColorName: item.ColorName || "",
              Mrp: item.Mrp || 0,
              Price: item.OurPrice,
              DiscountPercentage: item.DiscountPercentage || 0,
              Quantity: item.quantity,
              Total: item.OurPrice * item.quantity,
              Image1: item.Image1 || "",
              Image2: item.Image2 || "",
              Latitude: item.Latitude || "",
              Longitude: item.Longitude || "",
              AverageRating: item.AverageRating || 0,
              ReviewCount: item.ReviewCount || 0,
              Time: new Date().toISOString(),
              ...extraData,
            }),
          };
          break;

        case "update":
          url = `${baseApiURL}/api/cartitems/UpdateCartItem`;
          options = {
            method: "POST",
            headers,
            body: JSON.stringify({
              Id: item.serverId || 0,
              Quantity: item.quantity,
              Total: item.OurPrice * item.quantity,
              ...extraData,
            }),
          };
          break;

        case "remove":
          url = `${baseApiURL}/api/cartitems/DeleteCartItem/${
            item.serverId || 0
          }`;
          options = { method: "POST", headers };
          break;

        default:
          return;
      }

      const response = await apiFetch(url, options);
      if (!response.ok) throw new Error(`${action} failed`);
      return await response.json();
    } catch (error) {
      throw error;
    }
  };

  const addToCart = useCallback(
    async (product) => {
      try {
        // Validate required fields
        const requiredFields = [
          "ID",
          "ProductName",
          "OurPrice",
          "StoreMobile",
          "StoreName",
        ];
        const missingFields = requiredFields.filter((field) => !product[field]);

        if (missingFields.length > 0) {
          toast.error(`Missing required fields: ${missingFields.join(", ")}`, {
            autoClose: 3000,
            style: { backgroundColor: "#ff4444", color: "white" },
          });
          return;
        }

        if (product.stock <= 0) {
          toast.error(`${product.ProductName} is out of stock!`, {
            autoClose: 2000,
            style: { backgroundColor: "#ff4444", color: "white" },
          });
          return;
        }

        setCart((prevCart) => {
          // Check for store conflict
          if (
            prevCart.length > 0 &&
            prevCart[0].StoreMobile !== product.StoreMobile
          ) {
            showStoreConflictToast(
              prevCart[0].StoreName || "existing store",
              product
            );
            return prevCart;
          }

          // Check if item already exists in cart
          const existingItemIndex = prevCart.findIndex(
            (item) =>
              item.ID === product.ID &&
              item.Size === product.Size &&
              item.ColorName === product.ColorName
          );

          const quantityToAdd = product.quantity || 1;

          // If item exists, update quantity
          if (existingItemIndex !== -1) {
            const existingItem = prevCart[existingItemIndex];
            const newQuantity = existingItem.quantity + quantityToAdd;

            if (newQuantity > product.stock) {
              toast.warn(`Max quantity (${product.stock}) reached!`, {
                autoClose: 2000,
                toastId: `max-quantity-${product.ID}`,
                style: { backgroundColor: "#ffbb33", color: "white" },
              });
              return prevCart;
            }

            const updatedCart = [...prevCart];
            updatedCart[existingItemIndex] = {
              ...existingItem,
              quantity: newQuantity,
            };

            // Sync with server
            if (existingItem.serverId) {
              syncCartItemWithServer("update", updatedCart[existingItemIndex])
                .then((serverData) => {
                  if (serverData) {
                    setCart((currentCart) =>
                      currentCart.map((item) =>
                        item.ID === product.ID
                          ? { ...item, serverId: serverData.id }
                          : item
                      )
                    );
                  }
                })
                .catch((error) => {
                });
            }

            showUpdateToast(product.ProductName, newQuantity);
            return updatedCart;
          }
          // If item doesn't exist, add new item
          else {
            const newProduct = {
              ...product,
              quantity: quantityToAdd,
              addedAt: new Date().toISOString(),
            };

            const updatedCart = [...prevCart, newProduct];

            // Sync with server
            syncCartItemWithServer("add", newProduct)
              .then((serverData) => {
                if (serverData) {
                  setCart((currentCart) =>
                    currentCart.map((item) =>
                      item.ID === product.ID
                        ? { ...item, serverId: serverData.id }
                        : item
                    )
                  );
                }
              })
              .catch((error) => {
              });

            showAddToast(product.ProductName);
            setLastAddedItem(newProduct);
            startAbandonmentTimer();
            return updatedCart;
          }
        });

        window.dispatchEvent(new CustomEvent("cartUpdated"));
      } catch (error) {
        showErrorToast();
      }
    },
    [currentUser, startAbandonmentTimer]
  );

  const removeFromCart = useCallback(
    async (productId) => {
      setCart((prevCart) => {
        const itemToRemove = prevCart.find((item) => item.ID === productId);
        const updatedCart = prevCart.filter((item) => item.ID !== productId);

        if (itemToRemove?.serverId) {
          syncCartItemWithServer("remove", itemToRemove).catch((error) => {
            setCart((prevCart) => [...prevCart, itemToRemove]);
          });
        }

        toast.success("Item removed from cart", {
          autoClose: 2000,
          style: { backgroundColor: "#4CAF50", color: "white" },
        });
        return updatedCart;
      });
    },
    [currentUser]
  );

  const clearCart = useCallback(async () => {
    try {
      // Clear local state and storage first for immediate UI response
      resetAbandonmentTimer();
      setCart([]);
      localStorage.removeItem("cart");

      // Clear from server if user is logged in
      if (currentUser) {
        // First fetch the current cart from server
        const response = await apiFetch(
          `${baseApiURL}/api/cartitems/GetCartItems/${currentUser}`
        );

        if (!response.ok) {
          throw new Error("Failed to fetch cart items from server");
        }

        const serverCart = await response.json();

        // Delete all items from server
        const deletePromises = serverCart.map((item) =>
          fetch(`${baseApiURL}/api/cartitems/DeleteCartItem/${item.Id}`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          })
        );

        await Promise.all(deletePromises);
      }

      toast.success("Cart cleared", {
        autoClose: 2000,
        style: { backgroundColor: "#4CAF50", color: "white" },
      });
    } catch (error) {
      toast.error("Failed to clear cart completely", {
        autoClose: 3000,
        style: { backgroundColor: "#ff4444", color: "white" },
      });

      // If error occurs, try to restore local cart from localStorage
      const savedCart = localStorage.getItem("cart");
      if (savedCart) {
        setCart(JSON.parse(savedCart));
      }
    }
  }, [currentUser, resetAbandonmentTimer]);

  const updateQuantity = useCallback(
    async (productId, newQuantity) => {
      if (newQuantity < 1) {
        toast.error("Quantity cannot be less than 1", {
          autoClose: 2000,
          style: { backgroundColor: "#ff4444", color: "white" },
        });
        return;
      }

      setCart((prevCart) =>
        prevCart.map((item) => {
          if (item.ID === productId) {
            const updatedItem = { ...item, quantity: newQuantity };

            if (updatedItem.serverId) {
              syncCartItemWithServer("update", updatedItem).catch((error) => {
                setCart((prevCart) =>
                  prevCart.map((prevItem) =>
                    prevItem.ID === productId ? item : prevItem
                  )
                );
              });
            }

            return updatedItem;
          }
          return item;
        })
      );
    },
    [currentUser]
  );

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotal = cart.reduce(
    (sum, item) => sum + item.OurPrice * item.quantity,
    0
  );

  const showStoreConflictToast = (existingStore, product) => {
    toast.error(
      <div style={{ padding: "12px", fontFamily: "'Segoe UI', sans-serif" }}>
        <div
          style={{ fontSize: "16px", fontWeight: "600", marginBottom: "8px" }}
        >
          Can't add from different store!
        </div>
        <div style={{ marginBottom: "12px" }}>
          Your cart already contains items from <strong>{existingStore}</strong>
          . You can't add items from <strong>{product.StoreName}</strong> to the
          same cart.
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={() => {
              toast.dismiss();
              clearCart().then(() => addToCart(product));
            }}
            style={toastButtonStyle("#e74c3c", "#fff")}
          >
            Clear Cart & Add
          </button>
          <button
            onClick={() => toast.dismiss()}
            style={toastButtonStyle("#95a5a6", "#fff")}
          >
            Cancel
          </button>
        </div>
      </div>,
      {
        autoClose: 10000,
        style: {
          background: "linear-gradient(135deg, #ff4d4d 0%, #f94444 100%)",
          color: "white",
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(255, 76, 76, 0.25)",
          borderLeft: "4px solid #fff",
          padding: "0",
        },
        bodyStyle: { padding: "0" },
        closeButton: false,
      }
    );
  };

  const showUpdateToast = (productName, newQuantity) => {
    toast.success(`Updated quantity for ${productName} to ${newQuantity}`, {
      toastId: `update-${productName}`,
      autoClose: 2000,
      style: { backgroundColor: "#4CAF50", color: "white" },
    });
  };

  const showAddToast = (productName) => {
    toast.success(
      <div style={{ display: "flex", alignItems: "center" }}>
        Added <strong style={{ margin: "0 6px" }}>{productName}</strong> to cart
        <button
          onClick={() => (window.location.href = "/cartScreen")}
          style={{
            marginLeft: "10px",
            padding: "5px 10px",
            backgroundColor: "#ffffff",
            color: "#2ecc71",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontWeight: "bold",
          }}
        >
          Checkout Now
        </button>
      </div>,
      {
        autoClose: 3000,
        style: {
          backgroundColor: "#2ecc71",
          color: "#ffffff",
        },
      }
    );
  };

  const showErrorToast = () => {
    toast.error("Something went wrong while adding to cart", {
      autoClose: 3000,
      style: { backgroundColor: "#ff4444", color: "white" },
    });
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        clearCart,
        updateQuantity,
        cartCount,
        cartTotal,
        resetAbandonmentTimer,
        isSyncing,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
